/* eslint-disable no-unused-vars */
import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Grid, Stack, Typography, TextField, Button } from '@mui/material';
import OnboardingLayout from 'layout/OnboardingLayout';
import AuthCardWrapper from '../AuthCardWrapper';
import LogoBg from '../../../../assets/images/logo_bg.png';
import authService from 'api/authService';

const EmailOTP = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const tempToken = state?.tempToken;
    const email = state?.email;

    const OTP_LENGTH = 4;
    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
    const inputRefs = useRef([]);
    const [timer, setTimer] = useState(60);
    const [loading, setLoading] = useState(false);
    const [serverError, setServerError] = useState('');

    // Redirect if user reloads page
    useEffect(() => {
        if (!tempToken || !email) {
            navigate('/register');
        }
    }, []);

    // Timer countdown
    useEffect(() => {
        if (timer <= 0) return;
        const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
        return () => clearInterval(interval);
    }, [timer]);

    const handleChange = (index, value) => {
        if (/^\d?$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            // Move focus forward/back
            if (value && index < OTP_LENGTH - 1) inputRefs.current[index + 1]?.focus();
            if (!value && index > 0) inputRefs.current[index - 1]?.focus();
        }
    };

    const handleSubmit = async () => {
        setServerError('');
        const otpCode = otp.join('');
        if (otpCode.length !== OTP_LENGTH) return setServerError('Please enter the complete OTP.');

        setLoading(true);

        try {
            const res = await authService.verifyRegistrationOtp(tempToken, otpCode);

            console.log('OTP Verified', res);

            navigate('../vendor-information', {
                state: { tempToken: res.token, email }
            });
        } catch (err) {
            const message = err?.response?.data?.message || 'OTP verification failed. Please try again.';
            setServerError(message);
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setServerError('');
        setOtp(Array(OTP_LENGTH).fill(''));
        authService.registerEmail(email);
        inputRefs.current[0]?.focus();
        setTimer(60);
    };

    return (
        <OnboardingLayout
            step={2}
            totalSteps={3}
            title="Verify Your Email"
            subtitle={`Enter the OTP sent to ${email}`}
            backLink="/register"
            imageSrc={LogoBg}
        >
            <Stack spacing={3}>
                <AuthCardWrapper>
                    <Stack spacing={2} alignItems="center">
                        <Stack direction="row" spacing={2} justifyContent="center">
                            {otp.map((digit, index) => (
                                <TextField
                                    key={index}
                                    inputRef={(el) => (inputRefs.current[index] = el)}
                                    value={digit}
                                    onChange={(e) => handleChange(index, e.target.value)}
                                    inputProps={{
                                        maxLength: 1,
                                        style: { textAlign: 'center', fontSize: 24, width: 50 }
                                    }}
                                    variant="outlined"
                                />
                            ))}
                        </Stack>
                        {serverError && (
                            <Typography color="error" variant="body2">
                                {serverError}
                            </Typography>
                        )}
                        <Button variant="contained" size="large" onClick={handleSubmit} fullWidth disabled={loading} sx={{ mt: 2 }}>
                            {loading ? 'Verifying...' : 'Verify OTP'}
                        </Button>

                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {timer > 0 ? (
                                `Resend OTP in ${timer}s`
                            ) : (
                                <Button onClick={handleResend} variant="text">
                                    Resend OTP
                                </Button>
                            )}
                        </Typography>
                    </Stack>
                </AuthCardWrapper>
            </Stack>
        </OnboardingLayout>
    );
};

export default EmailOTP;
